/* ── BRIEF 126 §7 — IS THERE AN OUTLOOK TO OPEN AT ALL ──────────────────────
 *
 * [MEASURED at the glance of #431] 125's control answered "Cet e-mail ne peut
 * pas être ouvert d'ici — Outlook n'en donne aucun lien (not_connected)". That
 * refusal is CORRECT and it is 125's fence working. But `not_connected` is the
 * STANDING STATE OF THIS BOX BY RULING — the credentials doctrine
 * (`RULINGS.md:1186`): "staging and lab never hold live M365/Graph
 * credentials". So the affordance could never succeed here and always refused.
 *
 * [RULED] "When M365 is not connected at all, the affordance is ABSENT, not
 * present-and-refusing." Every OTHER refusal path — a `message:` URI,
 * `not_found`, `no_link`, a 502 from Graph — keeps its named message exactly as
 * 125 built it. This answers one question and only one: is there an account
 * behind the button.
 *
 * ── WHY A MODULE, AND WHY A CACHE ──────────────────────────────────────────
 *
 * `GET /auth/microsoft/status` is a per-user, per-session fact
 * (`routes/microsoft.js:19` — one `M365Token` row lookup). Three components
 * already fetch it independently, each on its own mount: `SettingsView:903`,
 * `CaseEmailsSection:158`, `CollectiveBulkMailerModal:172`. Those THREE ARE
 * DELIBERATELY NOT TOUCHED — "extract, don't improve" — but a fourth caller
 * that fires once per email control on a table of time entries would be a
 * request per row, so this one is asked once and shared.
 *
 * ONE IN-FLIGHT PROMISE, and a failure answers FALSE rather than throwing: a
 * status call that cannot be made is not evidence of a connection, and the
 * consequence of being wrong is a control that is missing rather than one that
 * lies.
 *
 * ── THE THIRD STATE IS THE POINT ───────────────────────────────────────────
 *
 * `null` — "not answered yet" — is NOT collapsed into `false`, because a
 * consumer has to be able to decide what to do while the answer is in flight,
 * and the two consumers this has decide differently. The button renders
 * nothing until it knows; the drop's document row is written from a value
 * already resolved.
 */
import { useEffect, useState } from "react";
import { api } from "../api";

let inflight: Promise<boolean> | null = null;
let answer: boolean | null = null;
const waiting = new Set<(v: boolean) => void>();

/** Ask once per session. Subsequent callers get the same promise. */
export function m365Connected(): Promise<boolean> {
  if (answer !== null) return Promise.resolve(answer);
  if (!inflight) {
    inflight = (api.get("/auth/microsoft/status") as Promise<{ connected?: boolean } | null>)
      .then((d: { connected?: boolean } | null) => !!(d && d.connected))
      .catch(() => false)
      .then((v: boolean) => {
        answer = v;
        for (const f of waiting) f(v);
        waiting.clear();
        return v;
      });
  }
  return inflight;
}

/** The cached answer without asking — `null` until the first call resolves. */
export function m365ConnectedNow(): boolean | null {
  return answer;
}

/** `null` while the answer is in flight, then `true`/`false`. Asks on mount. */
export function useM365Connected(): boolean | null {
  const [v, setV] = useState<boolean | null>(answer);
  useEffect(() => {
    if (answer !== null) {
      setV(answer);
      return undefined;
    }
    let live = true;
    const push = (x: boolean) => {
      if (live) setV(x);
    };
    waiting.add(push);
    /* Not swallowed (`no-silent-catch`): `m365Connected` already resolves
       false on failure and never rejects, so there is nothing here to hide —
       this `.catch` exists only because an un-awaited promise in an effect is
       a lint the tree enforces, and it re-states the same answer. */
    m365Connected().then(push).catch(() => push(false));
    return () => {
      live = false;
      waiting.delete(push);
    };
  }, []);
  return v;
}

/** Test seam — the drive and any future selftest need to be able to re-ask. */
export function __resetM365StatusCache() {
  inflight = null;
  answer = null;
  waiting.clear();
}
